'use client';

import { dataLayerPush } from '@/helper/tracking';
import { useEffect } from 'react';

function getInteractiveTarget(start: EventTarget | null): HTMLElement | null {
  if (!(start instanceof HTMLElement)) return null;
  return (
    start.closest<HTMLElement>(
      'button, a, [role="button"], input[type="button"], input[type="submit"], [data-gtm-event]'
    ) || null
  );
}

export default function GtmClickBridge() {
  useEffect(() => {
    // If GTM is already present, do nothing
    if (
      typeof window !== 'undefined' &&
      window.google_tag_manager &&
      Object.keys(window.google_tag_manager).length > 0
    ) {
      return;
    }

    let stopped = false;
    const captureOptions: AddEventListenerOptions = { capture: true, passive: true };
    const timer: { id: number | undefined } = { id: undefined };

    const handler = (e: MouseEvent) => {
      if (stopped) return;
      const el = getInteractiveTarget(e.target);
      if (!el) return;

      const eventName = el.dataset.gtmEvent || 'gtm_bridge_dom_click';
      const tag = el.tagName.toLowerCase();
      const text = (el.getAttribute('aria-label') || el.textContent || '').trim().slice(0, 120);
      const href =
        (el as HTMLAnchorElement).href || el.getAttribute('href') || (el as HTMLInputElement).formAction || undefined;

      dataLayerPush({
        event: eventName,
        source: 'gtm_bridge',
        category: el.dataset.gtmCategory || 'interaction',
        label: el.dataset.gtmLabel || text || undefined,
        value: el.dataset.gtmValue || undefined,
        element_id: el.id || undefined,
        element_tag: tag,
        element_classes: el.className || undefined,
        href,
        text: text || undefined,
        page_path: location.pathname,
        page_title: document.title,
      });
    };

    const onReady = () => stop();

    const stop = () => {
      if (stopped) return;
      stopped = true;
      document.removeEventListener('click', handler, captureOptions);
      window.removeEventListener('gtm-ready', onReady as EventListener);
      if (typeof timer.id === 'number') {
        window.clearInterval(timer.id);
      }
    };

    document.addEventListener('click', handler, captureOptions);

    window.addEventListener('gtm-ready', onReady as EventListener, { once: true });

    timer.id = window.setInterval(() => {
      if (window.google_tag_manager && Object.keys(window.google_tag_manager).length > 0) {
        stop();
      }
    }, 500);

    return () => {
      stop();
    };
  }, []);

  return null;
}
